<template>
  <div>
    <PageHeader
      v-if="!embedded"
      title="Metric Report"
      :subtitle="`Metric ${metricId} through the generic entity and fact identifier engine`"
      :breadcrumbs="[{ label: 'Beta' }]"
    />

    <div class="px-6 pt-4">
      <div class="flex items-end gap-4 border border-gray-200 bg-white rounded-lg p-4">
        <div>
          <label class="block text-xs font-semibold text-gray-600 mb-1">Entity ID scope</label>
          <input
            v-model="entityIdInput"
            type="number"
            class="w-40 px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-primary-400"
            placeholder="Whole tenant"
          />
        </div>
        <button class="btn-primary-sm" :disabled="loading" @click="applyEntityScope">
          Apply page scope
        </button>
        <button v-if="entityId !== null" class="text-sm text-gray-500 hover:text-gray-800" @click="clearEntityScope">
          Clear
        </button>
        <div class="text-xs text-gray-400 pb-2">
          Mirrors the entity context normally supplied by the page URL.
        </div>
      </div>
    </div>

    <div class="p-6">
      <section class="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <ComponentHeader title="Performance Summary" :icon="['fas', 'chart-line']">
          <button class="btn-accent-sm" :disabled="loading" @click="loadData">
            {{ loading ? 'Running…' : 'Refresh' }}
          </button>
        </ComponentHeader>

        <div class="px-4 py-3 border-b border-gray-200 flex items-center gap-2 flex-wrap">
          <span class="text-xs font-semibold text-gray-600">Entity Types</span>
          <select
            class="px-2 py-1.5 text-xs border border-dashed border-gray-300 rounded bg-white text-gray-500"
            @change="addGrouping($event.target.value); $event.target.value = ''"
          >
            <option value="">+ Entity Type</option>
            <optgroup label="Entity Type">
              <option v-for="type in availableEntityTypes" :key="`entity:${type}`" :value="`entity:${type}`">
                {{ type }}
              </option>
            </optgroup>
            <optgroup label="Fact Identifier">
              <option v-for="dimension in availableIdentifierGroupings" :key="`identifier:${dimension.key}`" :value="`identifier:${dimension.key}`">
                {{ dimension.label }}
              </option>
            </optgroup>
          </select>
          <button
            v-for="grouping in activeGroupingChips"
            :key="grouping.key"
            class="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-normal text-gray-700"
            @click="removeGrouping(grouping)"
          >
            {{ grouping.label }} <span class="text-gray-400">×</span>
          </button>
          <label class="inline-flex items-center gap-1.5 ml-auto text-xs text-gray-600">
            <input v-model="latestObservationOnly" type="checkbox" class="rounded" />
            Latest observation date
          </label>
        </div>

        <div class="p-4">
          <LoadingIndicator v-if="loading" message="Loading performance summary…" />
          <div v-else-if="error" class="min-h-[300px] rounded-lg bg-red-50 p-4 text-sm text-red-800">{{ error }}</div>
          <template v-else-if="summary">
            <div class="flex gap-3 mb-4 text-xs text-gray-500">
              <span>{{ formatCount(summary.row_count) }} rows</span>
              <span>{{ formatCount(summary.entity_count) }} entities</span>
              <span>{{ summary.duration_ms }} ms</span>
            </div>

            <div class="overflow-auto max-h-[620px] border border-gray-200 rounded-lg">
              <table class="w-full text-xs">
                <thead class="sticky top-0 bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th v-if="entityTypesSelected.length || noGroupings" class="px-3 py-2 text-left">{{ entityTypesSelected.length === 1 ? entityTypesSelected[0] : 'Entity' }}</th>
                    <th v-if="metricIds.length > 1" class="px-3 py-2 text-left">Metric</th>
                    <th class="px-3 py-2 text-left">Observation Date</th>
                    <th v-for="column in activeGroupColumns" :key="column.key" class="px-3 py-2 text-left">{{ column.label }}</th>
                    <th class="px-3 py-2 text-right">Numerator</th>
                    <th class="px-3 py-2 text-right">Denominator</th>
                    <th class="px-3 py-2 text-right">Rate</th>
                    <th class="px-3 py-2 text-right">Attribution</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                  <tr v-for="(row, index) in rows" :key="`${row.metric_id}:${row.entity_id}:${index}`">
                    <td v-if="entityTypesSelected.length || noGroupings" class="px-3 py-2">
                      <template v-if="entityTypesSelected.length">
                        <div class="font-medium text-gray-900">{{ row.entity_name }}</div>
                        <div class="font-mono text-[10px] text-gray-400">#{{ row.entity_id }}</div>
                      </template>
                      <span v-else class="font-medium text-gray-900">Overall</span>
                    </td>
                    <td v-if="metricIds.length > 1" class="px-3 py-2">{{ row.metric_name }}</td>
                    <td class="px-3 py-2">{{ groupValue(row, 'OBSERVATION_DATE') }}</td>
                    <td v-for="column in activeGroupColumns" :key="column.key" class="px-3 py-2">{{ groupValue(row, column.key) }}</td>
                    <td class="px-3 py-2 text-right tabular-nums">{{ formatCount(row.numerator) }}</td>
                    <td class="px-3 py-2 text-right tabular-nums">{{ formatCount(row.denominator) }}</td>
                    <td class="px-3 py-2 text-right tabular-nums">{{ formatRate(row.rate) }}</td>
                    <td class="px-3 py-2 text-right tabular-nums">{{ formatCount(row.attribution) }}</td>
                  </tr>
                  <tr v-if="!rows.length">
                    <td :colspan="activeGroupColumns.length + 6" class="px-3 py-8 text-center text-gray-400">No rows returned.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

export default {
  name: 'MetricTest',
  components: { PageHeader, ComponentHeader, LoadingIndicator },
  props: {
    metricId: { type: Number, default: 4 },
    embedded: { type: Boolean, default: false },
    configuredGroupers: { type: Array, default: () => ['CONTRACT_ID', 'OBSERVATION_DT_YEAR'] },
  },
  data() {
    return {
      loading: false,
      error: null,
      rows: [],
      summary: null,
      dimensions: [],
      entityTypes: ['Practice', 'Staff', 'TIN', 'Pods', 'Regions', 'Contract'],
      entityTypesSelected: [],
      entityIdInput: '',
      entityId: null,
      metricIds: [this.metricId],
      groupBy: [],
      filters: {},
      latestObservationOnly: true,
    };
  },
  computed: {
    noGroupings() {
      return this.entityTypesSelected.length === 0 && this.groupBy.length === 0;
    },
    availableEntityTypes() {
      return this.entityTypes.filter((type) => !this.entityTypesSelected.includes(type));
    },
    availableIdentifierGroupings() {
      return this.dimensions.filter((dimension) => !this.groupBy.includes(dimension.key));
    },
    activeGroupingChips() {
      return [
        ...this.entityTypesSelected.map((type) => ({ key: `entity:${type}`, label: type, kind: 'entity', value: type })),
        ...this.activeGroupColumns.map((column) => ({
          key: `identifier:${column.key}`,
          label: column.label,
          kind: 'identifier',
          value: column.key,
        })),
      ];
    },
    activeGroupColumns() {
      const byKey = Object.fromEntries(this.dimensions.map((dimension) => [dimension.key, dimension]));
      return this.groupBy.map((key) => byKey[key] || { key, label: key.replaceAll('_', ' ') });
    },
  },
  watch: {
    configuredGroupers: {
      deep: true,
      handler(values) {
        this.groupBy = this.groupBy.filter((value) => values.includes(value));
      },
    },
    metricId(value) {
      this.metricIds = [value];
      this.loadData();
    },
    entityTypesSelected: { deep: true, handler() { this.loadData(); } },
    groupBy: { deep: true, handler() { this.loadData(); } },
    latestObservationOnly() { this.loadData(); },
  },
  mounted() { this.loadData(); },
  methods: {
    addGrouping(value) {
      if (!value) return;
      const [kind, grouping] = value.split(':');
      if (kind === 'entity' && !this.entityTypesSelected.includes(grouping)) {
        this.entityTypesSelected = [...this.entityTypesSelected, grouping];
      }
      else if (kind === 'identifier' && !this.groupBy.includes(grouping)) {
        this.groupBy = [...this.groupBy, grouping];
      }
    },
    removeGrouping(grouping) {
      if (grouping.kind === 'entity') {
        this.entityTypesSelected = this.entityTypesSelected.filter((type) => type !== grouping.value);
      } else if (grouping.kind === 'identifier') {
        this.groupBy = this.groupBy.filter((value) => value !== grouping.value);
      }
    },
    applyEntityScope() {
      this.entityId = this.entityIdInput === '' ? null : Number(this.entityIdInput);
      this.loadData();
    },
    clearEntityScope() {
      this.entityIdInput = '';
      this.entityId = null;
      this.loadData();
    },
    async loadData() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.post(
          'api/impact_analytics/composable-metrics/metric-entity-report/',
          {
            metric_ids: this.metricIds,
            observation_year: 2026,
            entity_id: this.entityId,
            entity_types: this.entityTypesSelected,
            group_by: this.groupBy,
            filters: this.filters,
            latest_observation_only: this.latestObservationOnly,
          },
        );
        this.rows = data.results || [];
        this.summary = data.summary || null;
        this.dimensions = data.dimensions || [];
      } catch (error) {
        this.error = error?.response?.data?.error || error.message;
      } finally {
        this.loading = false;
      }
    },
    groupValue(row, key) {
      const value = row[key.toLowerCase()];
      if (value === null || value === undefined) return '—';
      return String(value).slice(0, 10);
    },
    formatCount(value) {
      return value === null || value === undefined ? '—' : Number(value).toLocaleString();
    },
    formatRate(value) {
      return value === null || value === undefined ? '—' : `${(Number(value) * 100).toFixed(1)}%`;
    },
  },
};
</script>
